<template>
  <div class="min-h-screen flex items-center justify-center px-4">
    <div class="max-w-md w-full space-y-8">
      <!-- Logo/Header -->
      <div class="text-center">
        <h1 class="text-4xl font-bold text-primary-600">🛒</h1>
        <h2 class="mt-4 text-3xl font-bold">{{ $t('common.appName') }}</h2>
        <p class="mt-2 text-gray-600">
          {{ isRegistering ? $t('auth.register.title') : $t('auth.login.title') }}
        </p>
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {{ errorMessage }}
      </div>

      <!-- Login/Register Form -->
      <form @submit.prevent="handleSubmit" class="mt-8 space-y-6">
        <div class="space-y-4">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('common.email') }}
            </label>
            <input
              id="email"
              v-model="email"
              type="email"
              required
              class="input"
              placeholder="you@example.com"
              :disabled="loading"
            />
          </div>

          <div>
            <div class="flex items-center justify-between mb-1">
              <label for="password" class="block text-sm font-medium text-gray-700">
                {{ $t('common.password') }}
              </label>
              <router-link
                v-if="!isRegistering"
                to="/forgot-password"
                class="text-sm text-primary-600 hover:text-primary-500"
              >
                {{ $t('auth.login.forgotPassword') }}
              </router-link>
            </div>
            <input
              id="password"
              v-model="password"
              type="password"
              required
              class="input"
              placeholder="••••••••"
              :disabled="loading"
            />
          </div>

          <div v-if="isRegistering">
            <label for="passwordConfirm" class="block text-sm font-medium text-gray-700 mb-1">
              {{ $t('auth.register.confirmPassword') }}
            </label>
            <input
              id="passwordConfirm"
              v-model="passwordConfirm"
              type="password"
              required
              class="input"
              placeholder="••••••••"
              :disabled="loading"
            />
          </div>
        </div>

        <button
          type="submit"
          class="w-full btn btn-primary"
          :disabled="loading"
        >
          {{ loading ? $t('common.pleaseWait') : (isRegistering ? $t('auth.register.button') : $t('auth.login.button')) }}
        </button>
      </form>

      <!-- Google Sign In -->
      <div class="relative">
        <div class="absolute inset-0 flex items-center">
          <div class="w-full border-t border-gray-300"></div>
        </div>
        <div class="relative flex justify-center text-sm">
          <span class="px-2 bg-gray-50 text-gray-500">{{ $t('auth.login.orContinueWith') }}</span>
        </div>
      </div>

      <button
        @click="showGoogleOAuthModal = true"
        class="w-full flex items-center justify-center gap-3 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        :disabled="loading"
      >
        <svg class="w-5 h-5" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        <span class="font-medium">Google</span>
      </button>

      <!-- Toggle Login/Register -->
      <div class="text-center">
        <button
          type="button"
          @click="toggleMode"
          class="text-primary-600 hover:text-primary-500 font-medium"
          :disabled="loading"
        >
          {{ isRegistering ? $t('auth.register.haveAccount') : $t('auth.login.noAccount') }}
        </button>
      </div>

      <!-- Footer -->
      <div class="text-center pt-4 border-t border-gray-100">
        <p class="text-xs text-gray-400">
          {{ $t('about.copyright', { year: currentYear }) }} &middot;
          <button
            type="button"
            @click="showAboutModal = true"
            class="text-primary-500 hover:text-primary-600 transition-colors"
          >
            {{ $t('about.contact') }}
          </button>
        </p>
      </div>
    </div>

    <!-- Google OAuth Modal -->
    <GoogleOAuthModal
      v-if="showGoogleOAuthModal"
      @confirm="handleGoogleLogin"
      @cancel="showGoogleOAuthModal = false"
    />

    <!-- About Modal -->
    <AboutModal
      v-if="showAboutModal"
      @close="showAboutModal = false"
    />
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '../composables/useAuth'
import GoogleOAuthModal from '../components/GoogleOAuthModal.vue'
import AboutModal from '../components/AboutModal.vue'

const router = useRouter()
const { login, register } = useAuth()

const isRegistering = ref(false)
const email = ref('')
const password = ref('')
const passwordConfirm = ref('')
const loading = ref(false)
const errorMessage = ref('')
const showGoogleOAuthModal = ref(false)
const showAboutModal = ref(false)
const currentYear = new Date().getFullYear()

const toggleMode = () => {
  isRegistering.value = !isRegistering.value
  errorMessage.value = ''
}

const handleSubmit = async () => {
  errorMessage.value = ''
  loading.value = true

  try {
    let result
    if (isRegistering.value) {
      result = await register(email.value, password.value, passwordConfirm.value)
    } else {
      result = await login(email.value, password.value)
    }

    if (result.success) {
      router.push('/lists')
    } else {
      errorMessage.value = result.error
    }
  } finally {
    loading.value = false
  }
}

const handleGoogleLogin = () => {
  showGoogleOAuthModal.value = false
  window.location.href = '/accounts/google/login/'
}
</script>
